import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { useCommunity } from "@/contexts/CommunityContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft,
  Loader2,
  Plus,
  Info,
  Link,
  UserPen,
  Clock,
  Home,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";

type InvitationCategory = "TEMPORARY" | "PERMANENT" | "SERVICE";

const INVITATION_CATEGORIES = [
  { value: "TEMPORARY" as const, label: "Temporal", icon: Clock },
  { value: "PERMANENT" as const, label: "Permanente", icon: Home },
  { value: "SERVICE" as const, label: "Proveedor", icon: Briefcase },
];

interface CreateFormData {
  unitId: string;
  category: InvitationCategory;
  validFrom: string;
  validUntil: string;
  notes: string;
  guestName: string;
  guestEmail: string;
  guestDni: string;
  fillGuestDetails: boolean;
}

const defaultFormData: CreateFormData = {
  unitId: "",
  category: "TEMPORARY",
  validFrom: new Date().toISOString().split("T")[0],
  validUntil: new Date(Date.now() + 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0],
  notes: "",
  guestName: "",
  guestEmail: "",
  guestDni: "",
  fillGuestDetails: false,
};

export function CreateInvitationPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { selectedCommunityId } = useCommunity();

  const [formData, setFormData] = useState<CreateFormData>(defaultFormData);
  const [showNotes, setShowNotes] = useState(false);

  const handleCategoryChange = (category: InvitationCategory) => {
    const now = new Date();
    const today = now.toISOString().split("T")[0];
    const nextDay = new Date(now);
    nextDay.setDate(nextDay.getDate() + 1);
    const tomorrow = nextDay.toISOString().split("T")[0];

    setFormData((prev) => ({
      ...prev,
      category,
      ...(category === "TEMPORARY" && {
        validFrom: today,
        validUntil: tomorrow,
      }),
      ...(category === "SERVICE" && {
        validFrom: today,
        validUntil: today,
      }),
    }));
  };

  const { data: communities, isLoading: communitiesLoading } =
    trpc.amenity.getUserCommunities.useQuery();

  const { data: units } = trpc.invitation.getUserUnits.useQuery(
    { communityId: selectedCommunityId || "" },
    { enabled: !!selectedCommunityId },
  );

  // Use single unit as default if user has only one
  const effectiveUnitId =
    formData.unitId || (units?.length === 1 ? units[0].id : "");

  const createInvitation = trpc.invitation.create.useMutation({
    onSuccess: (data) => {
      utils.invitation.getMyInvitations.invalidate();
      toast.success("Invitación creada");

      const inviteUrl = `${window.location.origin}/invite/${data.token}`;
      navigator.clipboard.writeText(inviteUrl);
      toast.info("Enlace de invitación copiado al portapapeles");

      navigate({ to: "/invitations" });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCommunityId || !effectiveUnitId) return;

    const validUntil =
      formData.category === "PERMANENT" ? null : formData.validUntil;

    createInvitation.mutate({
      communityId: selectedCommunityId,
      unitId: effectiveUnitId,
      category: formData.category,
      validFrom: formData.validFrom,
      validUntil,
      notes: formData.notes || undefined,
      ...(formData.fillGuestDetails && {
        guestName: formData.guestName,
        guestEmail: formData.guestEmail,
        guestDni: formData.guestDni,
      }),
    });
  };

  const isFormValid =
    effectiveUnitId &&
    (!formData.fillGuestDetails ||
      (formData.guestName && formData.guestEmail && formData.guestDni));

  if (!communitiesLoading && (!communities || communities.length === 0)) {
    return (
      <div className="container mx-auto max-w-2xl px-4 py-8">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No sos miembro de ningún barrio. Contactá al administrador para
            obtener acceso.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!communitiesLoading && units && units.length === 0) {
    return (
      <div className="container mx-auto max-w-2xl px-4 py-8">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No tenés ninguna unidad asignada. Contactá al administrador de tu
            barrio.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl px-4 py-8">
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate({ to: "/invitations" })}
      >
        <ChevronLeft className="mr-2 h-4 w-4" />
        Volver a invitaciones
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Nueva invitación</CardTitle>
          <CardDescription>
            Creá una nueva invitación. El invitado recibirá un enlace para
            completar sus datos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="unit">Unidad</Label>
              <Select
                value={effectiveUnitId}
                onValueChange={(value) =>
                  setFormData({ ...formData, unitId: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar unidad" />
                </SelectTrigger>
                <SelectContent>
                  {units?.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.blockSection
                        ? `${unit.blockSection} - ${unit.unitNumber}`
                        : unit.unitNumber}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <div className="grid grid-cols-3 gap-3">
                {INVITATION_CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const isSelected = formData.category === cat.value;
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => handleCategoryChange(cat.value)}
                      className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition-colors ${
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-muted hover:border-muted-foreground/50"
                      }`}
                    >
                      <Icon className="h-6 w-6" />
                      <span className="text-sm font-medium">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="validFrom">Válida desde</Label>
                <Input
                  id="validFrom"
                  type="date"
                  value={formData.validFrom}
                  onChange={(e) =>
                    setFormData({ ...formData, validFrom: e.target.value })
                  }
                />
              </div>

              {formData.category !== "PERMANENT" && (
                <div className="space-y-2">
                  <Label htmlFor="validUntil">Válida hasta</Label>
                  <Input
                    id="validUntil"
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        validUntil: e.target.value,
                      })
                    }
                  />
                </div>
              )}
            </div>

            {showNotes || formData.notes ? (
              <div className="space-y-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea
                  id="notes"
                  placeholder="Notas adicionales..."
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowNotes(true)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                + Agregar notas
              </button>
            )}

            <div className="space-y-2">
              <Label>¿Cómo querés invitar al invitado?</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, fillGuestDetails: false })
                  }
                  className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition-colors ${
                    !formData.fillGuestDetails
                      ? "border-primary bg-primary/5"
                      : "border-muted hover:border-muted-foreground/50"
                  }`}
                >
                  <Link className="h-6 w-6" />
                  <span className="text-sm font-medium">Enviar enlace</span>
                  <span className="text-xs text-muted-foreground">
                    El invitado completa sus datos
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, fillGuestDetails: true })
                  }
                  className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition-colors ${
                    formData.fillGuestDetails
                      ? "border-primary bg-primary/5"
                      : "border-muted hover:border-muted-foreground/50"
                  }`}
                >
                  <UserPen className="h-6 w-6" />
                  <span className="text-sm font-medium">
                    Completar datos ahora
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Ingresá los datos del invitado
                  </span>
                </button>
              </div>
            </div>

            {formData.fillGuestDetails && (
              <div className="space-y-4 rounded-xl border p-4">
                <div className="space-y-2">
                  <Label htmlFor="guestName">Nombre del invitado</Label>
                  <Input
                    id="guestName"
                    value={formData.guestName}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        guestName: e.target.value,
                      })
                    }
                    placeholder="Nombre completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guestEmail">Email del invitado</Label>
                  <Input
                    id="guestEmail"
                    type="email"
                    value={formData.guestEmail}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        guestEmail: e.target.value,
                      })
                    }
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guestDni">DNI del invitado</Label>
                  <Input
                    id="guestDni"
                    value={formData.guestDni}
                    onChange={(e) =>
                      setFormData({ ...formData, guestDni: e.target.value })
                    }
                    placeholder="Número de DNI"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate({ to: "/invitations" })}
                disabled={createInvitation.isPending}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createInvitation.isPending || !isFormValid}
              >
                {createInvitation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                Crear invitación
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
